<script setup>
import { computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  nameLabel: {
    type: String,
    default: '',
  },
  namePlaceholder: {
    type: String,
    default: '',
  },
  descriptionLabel: {
    type: String,
    default: '',
  },
  descriptionPlaceholder: {
    type: String,
    default: '',
  },
  statusLabel: {
    type: String,
    default: '',
  },
  statusPlaceholder: {
    type: String,
    default: '',
  },
  nameErrorMessage: {
    type: String,
    default: '',
  },
  descriptionErrorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['validationChange']);

const policyName = defineModel('policyName', {
  type: String,
  default: '',
});

const description = defineModel('description', {
  type: String,
  default: '',
});

const enabled = defineModel('enabled', {
  type: Boolean,
  default: true,
});

const validationRules = {
  policyName: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, { policyName, description });

const isValid = computed(() => !v$.value.$invalid);

const nameError = computed(() =>
  v$.value.policyName.$error ? props.nameErrorMessage : ''
);
const descriptionError = computed(() =>
  v$.value.description.$error ? props.descriptionErrorMessage : ''
);

// Lets the parent reveal every inline error, e.g. after a submit attempt.
const touchAll = () => v$.value.$touch();
const resetValidation = () => v$.value.$reset();

defineExpose({ touchAll, resetValidation });

watch(
  isValid,
  () => {
    emit('validationChange', {
      isValid: isValid.value,
      section: 'baseInfo',
    });
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid grid-cols-1 gap-4">
    <Input
      v-model="policyName"
      type="text"
      :label="nameLabel"
      :placeholder="namePlaceholder"
      :message="nameError"
      :message-type="nameError ? 'error' : 'info'"
      data-test-id="policy-name"
      @blur="v$.policyName.$touch()"
    />
    <Input
      v-model="description"
      type="text"
      :label="descriptionLabel"
      :placeholder="descriptionPlaceholder"
      :message="descriptionError"
      :message-type="descriptionError ? 'error' : 'info'"
      data-test-id="policy-description"
      @blur="v$.description.$touch()"
    />

    <!-- Status Field -->
    <div v-if="statusLabel" class="flex items-center gap-6">
      <WithLabel :label="statusLabel" name="enabled" class="w-full">
        <div class="flex items-center gap-2">
          <Switch v-model="enabled" />
          <span class="text-sm text-n-slate-11">
            {{ statusPlaceholder }}
          </span>
        </div>
      </WithLabel>
    </div>
  </div>
</template>
